import { AxiosError } from 'axios';
import { Record } from 'immutable';
import { AnyAction } from 'redux';
import { createAction, handleActions } from 'redux-actions';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import {
  AxiosResponseType,
  boatList,
  getBoardingInfo,
  getLocAlimList,
  getLocation,
  getRoom,
  getRoomList,
  getRoomUsers,
  getUploadData,
} from 'services/api-service/center';
import { DeviceReport } from 'services/control-protocol';
import { MomentService } from 'services/moment-service';
import { IUserInfo } from './user';

const prefix = 'MAIN';

/**
 * @description ActionTypes
 * 액션타입
 */
export const ActionTypes = {
  INIT_STATE: `${prefix}/INIT_STATE`,

  GET_ROOMINFOS: `${prefix}/GET_ROOMINFOS`,
  SET_ROOMINFOS: `${prefix}/SET_ROOMINFOS`,
  GET_ROOM: `${prefix}/GET_ROOM`,
  SET_ROOM: `${prefix}/SET_ROOM`,
  CLEAR_ROOM: `${prefix}/CLEAR_ROOM`,
  SELECT_ROOM: `${prefix}/SELECT_ROOM`,
  GET_LOCATIONS: `${prefix}/GET_LOCATIONS`,
  SET_LOCATIONS: `${prefix}/SET_LOCATIONS`,
  UPDATE_LOCATIONS: `${prefix}/UPDATE_LOCATIONS`,
  CLEAR_LOCATIONS: `${prefix}/CLEAR_LOCATIONS`,
  GET_USERS: `${prefix}/GET_USERS`,
  SET_USERS: `${prefix}/SET_USERS`,
  CLEAR_USERS: `${prefix}/CLEAR_USERS`,

  ADD_PARTTICIPANT: `${prefix}/ADD_PARTTICIPANT`,
  REMOVE_PARTTICIPANT: `${prefix}/REMOVE_PARTTICIPANT`,

  ADD_PUBLISHER: `${prefix}/ADD_PUBLISHER`,
  REMOVE_PUBLISHER: `${prefix}/REMOVE_PUBLISHER`,
  CLEAR_PUBLISHER: `${prefix}/CLEAR_PUBLISHER`,

  ADD_RESCUE: `${prefix}/ADD_RESCUE`,
  SET_COMMAND: `${prefix}/SET_COMMAND`,
  ADD_MESSAGE: `${prefix}/ADD_MESSAGE`,

  SET_DEVICE_REPORT: `${prefix}/SET_DEVICE_REPORT`,
  REMOVE_DEVICE_REPORT: `${prefix}/REMOVE_DEVICE_REPORT`,
  CLEAR_DEVICE_REPORTS: `${prefix}/CLEAR_DEVICE_REPORTS`,

  UPDATE_LIST: `${prefix}/UPDATE_LIST`,

  GET_VESSELS: `${prefix}/GET_VESSELS`,
  SET_VESSELS: `${prefix}/SET_VESSELS`,

  GET_RESCUES: `${prefix}/GET_RESCUES`,
  SET_RESCUES: `${prefix}/SET_RESCUES`,

  GET_ALERTS: `${prefix}/GET_ALERTS`,
  SET_ALERTS: `${prefix}/SET_ALERTS`,

  GET_PASSENGERS: `${prefix}/GET_PASSENGERS`,
  SET_PASSENGERS: `${prefix}/SET_PASSENGERS`,
  CLEAR_PASSENGERS: `${prefix}/CLEAR_PASSENGERS`,
  UPDATE_NUMBER: `${prefix}/UPDATE_NUMBER`,
};

/**
 * @description ActionCtor
 * 액션 컨트롤러
 */
export const ActionCtor = {
  initState: createAction(ActionTypes.INIT_STATE),

  getRoomInfos: createAction(ActionTypes.GET_ROOMINFOS),
  selectRoom: createAction(ActionTypes.SELECT_ROOM),

  getRoom: createAction(ActionTypes.GET_ROOM),
  clearRoom: createAction(ActionTypes.CLEAR_ROOM),

  getLocations: createAction(ActionTypes.GET_LOCATIONS),
  updateLocations: createAction(ActionTypes.UPDATE_LOCATIONS),
  clearLocations: createAction(ActionTypes.CLEAR_LOCATIONS),

  getUsers: createAction(ActionTypes.GET_USERS),
  clearUsers: createAction(ActionTypes.CLEAR_USERS),

  addParticipant: createAction(ActionTypes.ADD_PARTTICIPANT),
  removeParticipant: createAction(ActionTypes.REMOVE_PARTTICIPANT),

  addPublisher: createAction(ActionTypes.ADD_PUBLISHER),
  removePublisher: createAction(ActionTypes.REMOVE_PUBLISHER),
  clearPublisher: createAction(ActionTypes.CLEAR_PUBLISHER),

  addRescue: createAction(ActionTypes.ADD_RESCUE),
  setCommand: createAction(ActionTypes.SET_COMMAND),
  addMessage: createAction(ActionTypes.ADD_MESSAGE),

  setDeviceReport: createAction(ActionTypes.SET_DEVICE_REPORT),
  removeDeviceReport: createAction(ActionTypes.REMOVE_DEVICE_REPORT),
  clearDeviceReports: createAction(ActionTypes.CLEAR_DEVICE_REPORTS),

  updateList: createAction(ActionTypes.UPDATE_LIST),

  getVessel: createAction(ActionTypes.GET_VESSELS),

  getRescues: createAction(ActionTypes.GET_RESCUES),

  getMissingLocationAlerts: createAction(ActionTypes.GET_ALERTS),

  getPassenger: createAction(ActionTypes.GET_PASSENGERS),
  setPassenger: createAction(ActionTypes.SET_PASSENGERS),
  clearPassenger: createAction(ActionTypes.CLEAR_PASSENGERS),
  updateRNumberPhones: createAction(ActionTypes.UPDATE_NUMBER),
};

/**
 * @description Saga
 * 사이드 이펙트 관리
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export function* MainSaga() {
  yield all([
    takeLatest(ActionTypes.GET_ROOMINFOS, function* (action: AnyAction) {
      try {
        const date = action.payload;
        // console.log('GET_ROOMINFOS date ', date);
        yield delay(1000);
        const userInfo: IUserInfo = yield select((state: any) => state.UserReducer.userInfo);
        const response: AxiosResponseType = yield call(
          getRoomList,
          userInfo.groupIdx,
          userInfo.userIdx,
          date === undefined ? MomentService.getInstance().getDateFormat(new Date(), 'YYYYMMDD') : date
        );
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_ROOMINFOS, payload: response.datas });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_LOCATIONS, function* (action: AnyAction) {
      try {
        const roomId = action.payload;
        const response: AxiosResponseType = yield call(getLocation, roomId);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_LOCATIONS, payload: response.data });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_USERS, function* (action: AnyAction) {
      try {
        const roomId = action.payload;
        yield delay(1000);
        const response: AxiosResponseType = yield call(getRoomUsers, roomId);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_USERS, payload: response });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_ROOM, function* (action: AnyAction) {
      try {
        const roomId = action.payload;
        const response: AxiosResponseType = yield call(getRoom, roomId);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_ROOM, payload: response.data });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_VESSELS, function* () {
      try {
        const response: AxiosResponseType = yield call(boatList);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_VESSELS, payload: response.it });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_RESCUES, function* () {
      try {
        const response: AxiosResponseType = yield call(getUploadData);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_RESCUES, payload: response.dataList });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_ALERTS, function* (action: AnyAction) {
      try {
        const date = action.payload;
        yield delay(1000);
        const response: AxiosResponseType = yield call(
          getLocAlimList,
          date === undefined ? MomentService.getInstance().getDateFormat(new Date(), 'YYYYMMDD') : date
        );
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_ALERTS, payload: response.data });
        }
      } catch (error) {
        const statusCode = (error as AxiosError)?.status || 500;
        yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
    takeLatest(ActionTypes.GET_PASSENGERS, function* (action: AnyAction) {
      try {
        const data = action.payload;
        const response: AxiosResponseType = yield call(getBoardingInfo, data[0], data[1]);
        if (response.result === 'success') {
          yield put({ type: ActionTypes.SET_PASSENGERS, payload: response.it });
        }
      } catch (error) {
        // const statusCode = (error as AxiosError)?.status || 500;
        // yield put({ type: ActionTypes.INIT_STATE, payload: { statusCode } });
      }
    }),
  ]);
}

/**
 * @description State
 * 상태 정의
 */
interface IMain {
  roomInfos: any[];
  locations: any[];
  curRoom: object | undefined;
  users: IUsers;
  selectedRoom: string | undefined;
  participants: any[];
  publishers: any[];
  rescue: any[];
  receivedCommand: object | undefined;
  receivedMessage: object | undefined;
  deviceReports: { [phone: string]: DeviceReport };
  checkUser: any[] | undefined;
  vessels: any[];
  rescues: any[];
  passengers: any[] | undefined;
  rNumberPhones: string[];
  missingLocationAlerts: any[];
}
interface IUsers {
  vDatas: any[];
  rDatas: any[];
}
const MainRecord = Record<IMain>({
  roomInfos: [],
  locations: [],
  curRoom: undefined,
  users: { vDatas: [], rDatas: [] },
  selectedRoom: undefined,
  participants: [],
  publishers: [],
  rescue: [],
  receivedCommand: undefined,
  receivedMessage: undefined,
  deviceReports: {},
  checkUser: undefined,
  vessels: [],
  rescues: [],
  passengers: undefined,
  rNumberPhones: [],
  missingLocationAlerts: [],
});
export class MainState extends MainRecord {}

/**
 * @description Reducer
 * 상태변화가 일어나는곳
 */
const initialState = new MainState();
const MainReducer = handleActions<MainState, AnyAction>(
  {
    [ActionTypes.INIT_STATE]: () => {
      return new MainState();
    },

    [ActionTypes.SET_ROOMINFOS]: (state, action) => {
      const roomInfos: any = action.payload;
      return state.set('roomInfos', roomInfos);
    },

    [ActionTypes.SET_ROOM]: (state, action) => {
      const curRoom: any = action.payload;
      return state.set('curRoom', curRoom);
    },

    [ActionTypes.CLEAR_ROOM]: (state, action) => {
      return state.set('curRoom', undefined);
    },

    [ActionTypes.SELECT_ROOM]: (state, action) => {
      const selectedRoom: any = action.payload;
      return state.set('selectedRoom', selectedRoom);
    },

    [ActionTypes.SET_LOCATIONS]: (state, action) => {
      const locations: any = action.payload;
      return state.set('locations', locations);
    },

    [ActionTypes.UPDATE_LOCATIONS]: (state, action) => {
      const locations = state.get('locations');
      const update: any = action.payload;
      const isMatch = locations.some((element: any) => {
        if (element.phone === update[0]) {
          return true;
        }
      });
      // console.log('UPDATE_LOCATIONS', isMatch);
      if (isMatch) {
        const addLocations = locations.map((element: any) => {
          if (element.phone === update[0]) {
            element.loc.push({
              acc: update[1].acc,
              heading: update[1].heading,
              lat: update[1].lat,
              lng: update[1].lng,
              saveTime: MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              speed: update[1].speed,
            });
          }
          return element;
        });
        return state.set('locations', [...addLocations]);
      } else {
        locations.push({
          phone: update[0],
          role: update[1].role,
          loc: [
            {
              acc: update[1].acc,
              heading: update[1].heading,
              lat: update[1].lat,
              lng: update[1].lng,
              saveTime: MomentService.getInstance().getDateFormat(new Date(), 'YYYY-MM-DD HH:mm:ss'),
              speed: update[1].speed,
            },
          ],
        });
        // console.log('UPDATE_LOCATIONS Add', locations);
        return state.set('locations', [...locations]);
      }
    },

    [ActionTypes.CLEAR_LOCATIONS]: (state, action) => {
      return state.set('locations', []);
    },

    [ActionTypes.SET_USERS]: (state, action) => {
      const { vDatas, rDatas } = action.payload;
      return state.set('users', { vDatas, rDatas });
    },
    [ActionTypes.CLEAR_USERS]: (state, action) => {
      const vDatas = [];
      const rDatas = [];
      return state.set('users', { vDatas, rDatas });
    },

    [ActionTypes.ADD_PARTTICIPANT]: (state, action) => {
      const participants = state.get('participants');
      participants.push(action.payload);
      return state.set('participants', [...participants]);
    },
    [ActionTypes.REMOVE_PARTTICIPANT]: (state, action) => {
      const participants = state.get('participants');
      const index = participants.findIndex((element) => element.name() === action.payload.name());
      if (index !== -1) {
        participants.splice(index, 1);
      }
      return state.set('participants', [...participants]);
    },

    [ActionTypes.ADD_PUBLISHER]: (state, action) => {
      const publishers = state.get('publishers');
      publishers.push(action.payload);
      return state.set('publishers', [...publishers]);
    },
    [ActionTypes.REMOVE_PUBLISHER]: (state, action) => {
      const publishers = state.get('publishers');
      const name = action.payload.name();
      const found = publishers.findIndex((element) => element.name() === name);
      if (found !== -1) {
        publishers.splice(found, 1);
      }
      const reports = { ...state.get('deviceReports') };
      delete reports[name];
      return state.set('publishers', [...publishers]).set('deviceReports', reports);
    },
    [ActionTypes.CLEAR_PUBLISHER]: (state, action) => {
      return state.set('publishers', []).set('deviceReports', {});
    },

    [ActionTypes.ADD_RESCUE]: (state, action) => {
      const rescue: any = action.payload;
      return state.set('rescue', rescue);
    },
    [ActionTypes.SET_COMMAND]: (state, action) => {
      const command: any = action.payload;
      return state.set('receivedCommand', command);
    },
    [ActionTypes.ADD_MESSAGE]: (state, action) => {
      const message: any = action.payload;
      return state.set('receivedMessage', message);
    },

    [ActionTypes.SET_DEVICE_REPORT]: (state, action) => {
      const report: any = action.payload;
      if (!report || !report.phone) {
        return state;
      }
      return state.set('deviceReports', { ...state.get('deviceReports'), [report.phone]: report });
    },
    [ActionTypes.REMOVE_DEVICE_REPORT]: (state, action) => {
      const phone: any = action.payload;
      const reports = { ...state.get('deviceReports') };
      delete reports[phone];
      return state.set('deviceReports', reports);
    },
    [ActionTypes.CLEAR_DEVICE_REPORTS]: (state) => {
      return state.set('deviceReports', {});
    },

    [ActionTypes.UPDATE_LIST]: (state, action) => {
      const checkUser: any = action.payload;
      return state.set('checkUser', checkUser);
    },
    [ActionTypes.SET_VESSELS]: (state, action) => {
      const vessel: any = action.payload;
      const sortedVessels = vessel.sort((a, b) => {
        if (a.state === b.state) return 0;
        if (a.state === 'A') return -1;
        if (b.state === 'A') return 1;
        return 0;
      });
      return state.set('vessels', sortedVessels);
    },
    [ActionTypes.SET_RESCUES]: (state, action) => {
      const rescues: any = action.payload;
      return state.set('rescues', rescues);
    },
    [ActionTypes.SET_ALERTS]: (state, action) => {
      const missingLocationAlerts: any = action.payload;
      return state.set('missingLocationAlerts', missingLocationAlerts);
    },
    [ActionTypes.SET_PASSENGERS]: (state, action) => {
      const passengers: any = action.payload;
      return state.set('passengers', passengers);
    },
    [ActionTypes.CLEAR_USERS]: (state, action) => {
      return state.set('passengers', undefined);
    },
    [ActionTypes.UPDATE_NUMBER]: (state, action) => {
      const rNumberPhones: any = action.payload;
      return state.set('rNumberPhones', rNumberPhones);
    },
  },
  initialState
);

export default MainReducer;
